import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import api from '../services/api';
import { notificaConferma, messaggioDa } from '../utils/notifiche';
import { dataIta } from '../utils/oraItaliana';
import '../styles/RiassegnaSomministrazioneConsole.css';

/**
 * Il «Riassegna» del compito dei prodotti usati (migration 292, risposta 7
 * di Alex del 3 ottobre 2026).
 *
 * Una visita e' nel cestino e chi l'ha cancellata ha risposto che i
 * prodotti sono stati usati: la somministrazione e' avvenuta, ma su un
 * altro paziente. Qui l'amministrazione sceglie il paziente giusto e la sua
 * prestazione, e il database sposta la somministrazione - gli scarichi, col
 * loro lotto - rimettendo in magazzino lo scarico della registrazione
 * rifatta: la merce esce una volta sola, il conto del medico segue chi ha
 * eseguito, la ricerca per lotto trova il paziente giusto.
 *
 * La schermata non decide niente: le prestazioni proposte sono quelle che
 * il database accetterebbe (riassegna_rifiuto), e il rifiuto, se arriva,
 * arriva dal server col suo perche'.
 */
const quanti = (q) => Number(q).toLocaleString('it-IT', { maximumFractionDigits: 4 });

const RiassegnaSomministrazione = ({ cancellazioneId, onChiudi, onFatto }) => {
    const [righe, setRighe] = useState(null);
    const [errore, setErrore] = useState(null);
    const [origine, setOrigine] = useState(null);
    const [cerca, setCerca] = useState('');
    const [pazienti, setPazienti] = useState([]);
    const [paziente, setPaziente] = useState(null);
    const [candidati, setCandidati] = useState(null);
    const [destinazione, setDestinazione] = useState(null);
    const [inCorso, setInCorso] = useState(false);

    useEffect(() => {
        let vivo = true;
        api.get(`/cestino/${cancellazioneId}/riassegna`)
            .then((r) => {
                if (!vivo) return;
                const elenco = r.data?.data?.righe || [];
                setRighe(elenco);
                if (elenco.length === 1) setOrigine(elenco[0].riga_id);
            })
            .catch((e) => { if (vivo) setErrore(messaggioDa(e, 'Non riesco a leggere che cosa c’è da riassegnare')); });
        return () => { vivo = false; };
    }, [cancellazioneId]);

    useEffect(() => {
        if (cerca.trim().length < 3) { setPazienti([]); return undefined; }
        let vivo = true;
        const t = setTimeout(async () => {
            try {
                const r = await api.get('/pazienti', { params: { search: cerca, limit: 8 } });
                if (vivo) setPazienti(r.data.data?.pazienti || []);
            } catch (e) { if (vivo) setPazienti([]); }
        }, 300);
        return () => { vivo = false; clearTimeout(t); };
    }, [cerca]);

    useEffect(() => {
        setCandidati(null);
        setDestinazione(null);
        if (!origine || !paziente) return undefined;
        let vivo = true;
        api.get(`/cestino/${cancellazioneId}/riassegna/candidati`,
                { params: { riga_origine: origine, paziente_id: paziente.id } })
            .then((r) => { if (vivo) setCandidati(r.data?.data?.candidati || []); })
            .catch((e) => { if (vivo) setErrore(messaggioDa(e, 'Non riesco a cercare le prestazioni del paziente')); });
        return () => { vivo = false; };
    }, [cancellazioneId, origine, paziente]);

    const riassegna = async () => {
        setInCorso(true);
        setErrore(null);
        try {
            const r = await api.post(`/cestino/${cancellazioneId}/riassegna`, {
                riga_origine: origine, riga_destinazione: destinazione, paziente_id: paziente.id,
            });
            notificaConferma(r.data?.message || 'Somministrazione riassegnata.');
            onFatto();
        } catch (e) {
            setErrore(messaggioDa(e, 'Non riesco a riassegnare la somministrazione'));
        } finally {
            setInCorso(false);
        }
    };

    const rigaScelta = (righe || []).find((x) => x.riga_id === origine);

    return (
        <div className="cmp-modale-fondo" onClick={inCorso ? undefined : onChiudi}>
            <div className="cmp-modale" onClick={(e) => e.stopPropagation()}
                 role="dialog" aria-modal="true" aria-labelledby="riassegna-titolo">
                <header>
                    <h3 id="riassegna-titolo">Riassegna la somministrazione</h3>
                    <button onClick={onChiudi} disabled={inCorso} aria-label="Chiudi"><X size={18} /></button>
                </header>

                {errore && <div className="cmp-errore">{errore}</div>}
                {righe === null && !errore && <p className="cmp-vuoto">Caricamento…</p>}
                {righe && righe.length === 0 && (
                    <p className="cmp-vuoto">Sulla visita nel cestino non resta niente da riassegnare.</p>
                )}

                {righe && righe.length > 0 && (
                    <>
                        <div className="cmp-campo">
                            <span>Che cosa spostare</span>
                            {righe.map((x) => (
                                <label key={x.riga_id} className="cmp-riassegna-scelta">
                                    <input type="radio" name="riassegna-origine" checked={origine === x.riga_id}
                                           onChange={() => setOrigine(x.riga_id)} />
                                    <span>
                                        <b>{x.prestazione}</b>
                                        {x.prodotti.map((p, i) => (
                                            <em key={i}>{p.prodotto} — lotto {p.codice_lotto || '—'}: {quanti(p.quantita)}</em>
                                        ))}
                                    </span>
                                </label>
                            ))}
                        </div>

                        <div className="cmp-campo">
                            <span>Il paziente giusto</span>
                            {paziente ? (
                                <div className="cmp-paziente-scelto">
                                    <span>{paziente.cognome} {paziente.nome}</span>
                                    <button onClick={() => { setPaziente(null); setCerca(''); }} disabled={inCorso}
                                            aria-label="Cambia paziente">
                                        <X size={14} />
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <input value={cerca} onChange={(e) => setCerca(e.target.value)}
                                           placeholder="Cerca: almeno tre lettere…" />
                                    {pazienti.length > 0 && (
                                        <ul className="cmp-risultati">
                                            {pazienti.map((p) => (
                                                <li key={p.id}>
                                                    <button onClick={() => { setPaziente(p); setPazienti([]); }}>
                                                        {p.cognome} {p.nome}
                                                        <em>{p.codice_fiscale || ''}</em>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </>
                            )}
                        </div>

                        {paziente && rigaScelta && (
                            <div className="cmp-campo">
                                <span>La sua prestazione</span>
                                {candidati === null && <p className="cmp-vuoto">Cerco le prestazioni…</p>}
                                {candidati && candidati.length === 0 && (
                                    <p className="cmp-riassegna-avviso">
                                        Nessuna prestazione di questo paziente, registrata dopo quella nel cestino, usa
                                        quei prodotti. Registra prima la procedura sul paziente giusto, poi torna qui.
                                        Una visita dello stesso paziente della visita nel cestino non si propone: lì la
                                        risposta giusta era «No».
                                    </p>
                                )}
                                {(candidati || []).map((c) => (
                                    <label key={c.riga_id} className="cmp-riassegna-scelta">
                                        <input type="radio" name="riassegna-destinazione" checked={destinazione === c.riga_id}
                                               onChange={() => setDestinazione(c.riga_id)} />
                                        <span>
                                            <b>{dataIta(c.data_visita)} — {c.prestazione}</b>
                                            {c.medico_cognome && <em>{c.medico_titolo || ''} {c.medico_cognome}</em>}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}

                        <em className="cmp-nota">
                            La somministrazione passa alla prestazione scelta col suo lotto, e i prodotti
                            che quella prestazione aveva scaricato tornano in magazzino: escono una volta sola.
                            Scegli la registrazione rifatta, non un altro trattamento dello stesso paziente.
                            Tutto resta nel registro delle modifiche.
                        </em>
                    </>
                )}

                <footer>
                    <button className="cmp-btn" onClick={onChiudi} disabled={inCorso}>Annulla</button>
                    <button className="cmp-btn primario" onClick={riassegna}
                            disabled={inCorso || !origine || !paziente || !destinazione}>
                        Riassegna
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default RiassegnaSomministrazione;
